import * as React from 'react';
import { FC, useState, useEffect, useCallback } from 'react';
import { Box, Typography, Link, useTheme } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { ActionButton } from './action-button';
import { LoadingState } from './loading-state';
import {
  useApi,
  useAnalytics,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { useAtom } from 'jotai';
import ReactMarkdown from 'react-markdown';
import { AutoFixConfig, AutofixStepResult } from '@internal/yardstick-common';
import { AutofixV2Step, AutoFixV2StepExecutionResponse } from './types';
import { yardstickApiRef } from '../../../api';
import { FontFamily } from '@principal-developer-portal/ui';
import { extractGitHubResourceDetails } from './analytics-utils';
import {
  optimisticRemediationStatusAtom,
  remediationVersionAtom,
} from '../../../hooks/use-filters';

interface AutofixV2RecommendationFlowProps {
  issueId: string;
  recommendationStep: AutoFixConfig;
  autofixStep: AutoFixConfig;
  onClose?: () => void;
  entityRef?: string;
  checkId?: string;
  isGroupedIssue?: boolean; // Flag for grouped issues in logical accordion
  forceExpandPreview?: boolean; // Force preview to be expanded (no collapse option)
  issueIds?: string[]; // NEW: Array of issue IDs for bulk execution (resource-grouped)
  _isResourceGrouped?: boolean; // NEW: Flag indicating resource-grouped execution (prefixed with _ as intentionally unused for now)
}

interface FlowState {
  recommendationResult: AutofixV2Step | null;
  autofixResult: AutofixV2Step | null;
  executing: boolean;
  loading: boolean;
  error: string | null;
  fetchError: string | null;
}

/**
 * Pattern 1: Multi-Step with Review (Recommendation + AutoFix)
 *
 * This component handles the 3-phase flow:
 * 1. Generate AI recommendation
 * 2. Review recommendation
 * 3. Apply autofix and view in GitHub
 */
export const AutofixV2RecommendationFlow: FC<
  AutofixV2RecommendationFlowProps
> = ({
  issueId,
  recommendationStep,
  autofixStep,
  onClose,
  entityRef,
  checkId,
  isGroupedIssue: _isGroupedIssue = false, // Unused: for future feature flag
  forceExpandPreview: _forceExpandPreview = false, // Unused: for future feature flag
  issueIds,
  _isResourceGrouped = false, // Unused: for future resource-grouped UI features
}) => {
  // Void unused variable to satisfy TypeScript
  void _isResourceGrouped;

  const yardstickApi = useApi(yardstickApiRef);
  const analytics = useAnalytics();
  const identityApi = useApi(identityApiRef);
  const theme = useTheme();
  const [, setOptimisticStatuses] = useAtom(optimisticRemediationStatusAtom);
  const [, setRemediationVersion] = useAtom(remediationVersionAtom);

  const [state, setState] = useState<FlowState>({
    recommendationResult: null,
    autofixResult: null,
    executing: false,
    loading: true,
    error: null,
    fetchError: null,
  });

  // Get user information for analytics
  const getUserInfo = useCallback(async (): Promise<string> => {
    try {
      const { displayName, email } = await identityApi.getProfileInfo();
      return displayName || email?.replace(/@.*/, '') || 'unknown';
    } catch {
      return 'unknown';
    }
  }, [identityApi]);

  // Fetch existing step results on mount
  const fetchStepResults = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true, fetchError: null }));
      const steps: AutofixStepResult[] =
        await yardstickApi.getAutofixV2StepResults(issueId);

      const recResult = steps.find(
        (s: AutofixStepResult) =>
          s.stepConfig?.handler === recommendationStep.handler,
      );
      const fixResult = steps.find(
        (s: AutofixStepResult) => s.stepConfig?.handler === autofixStep.handler,
      );

      // Map AutofixStepResult to AutofixV2Step
      const mapToV2Step = (
        result: AutofixStepResult | undefined,
      ): AutofixV2Step | null => {
        if (!result) return null;

        let status: 'success' | 'failed' | 'pending';
        if (result.status === 'success') {
          status = 'success';
        } else if (result.status === 'failed') {
          status = 'failed';
        } else {
          status = 'pending';
        }

        return {
          id: `${result.issueId}-${result.stepName}`,
          stepName: result.stepName,
          stepConfig: result.stepConfig,
          stepInputParams: result.stepInputParams,
          stepOutputParams: result.stepOutputParams,
          autofixText: result.autofixText,
          error_message: result.errorMessage,
          status,
          createdAt:
            result.createdAt instanceof Date
              ? result.createdAt.toISOString()
              : result.createdAt,
          updatedAt:
            result.updatedAt instanceof Date
              ? result.updatedAt.toISOString()
              : result.updatedAt,
        };
      };

      setState(prev => ({
        ...prev,
        loading: false,
        fetchError: null,
        recommendationResult: mapToV2Step(recResult),
        autofixResult: mapToV2Step(fixResult),
      }));
    } catch (error) {
      // Error fetching step results
      setState(prev => ({
        ...prev,
        loading: false,
        fetchError:
          error instanceof Error
            ? error.message
            : 'Failed to load auto remediation status',
      }));
    }
  }, [issueId, recommendationStep.handler, autofixStep.handler, yardstickApi]);

  // Execute a step (recommendation or autofix)
  const executeStep = useCallback(
    async (stepName: string, stepConfig: AutoFixConfig) => {
      const isRecommendation = stepName === recommendationStep.handler;

      // Compute retry status from state
      const isRetry = isRecommendation
        ? !!(
            state.recommendationResult &&
            state.recommendationResult.status === 'failed'
          )
        : !!(state.autofixResult && state.autofixResult.status === 'failed');

      // Determine step type for analytics
      let stepType: string;
      if (autofixStep.handler.includes('github-issue')) {
        stepType = 'github_issue';
      } else if (autofixStep.handler.includes('pull-request')) {
        stepType = 'github_pull_request';
      } else {
        stepType = 'auto_remediation';
      }

      // Track analytics before execution
      try {
        let actionType: string;
        if (isRecommendation) {
          actionType = isRetry ? 'generate_retry' : 'generate';
        } else {
          actionType = isRetry ? 'apply_retry' : 'apply';
        }

        const user = await getUserInfo();

        analytics.captureEvent(
          'insights:auto_remediation:user_action',
          'auto_remediation',
          {
            attributes: {
              entity: entityRef || 'unknown',
              checkId: checkId || 'unknown',
              issueId: issueId || 'unknown',
              actionType,
              stepType: isRecommendation ? 'recommendation' : stepType,
              isRetry,
              user,
            },
          },
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('failed to capture analytic event:', error);
      }
      try {
        setState(prev => ({ ...prev, executing: true, error: null }));

        // Optimistically mark as in-progress for the badge/menu
        setOptimisticStatuses(prev => ({ ...prev, [issueId]: 'in_progress' }));

        // Use bulk API if multiple issues (resource-grouped)
        const isBulkExecution = issueIds && issueIds.length > 1;
        const executionResponse: AutoFixV2StepExecutionResponse =
          isBulkExecution
            ? await yardstickApi.executeAutofixV2StepBulk(
                issueIds!,
                stepName,
                stepConfig.params || {},
              )
            : await yardstickApi.executeAutofixV2Step(issueId, {
                stepName: stepName,
                params: stepConfig.params || {},
              });

        const newStepResult: AutofixV2Step = {
          id: `${issueId}-${stepName}`,
          stepName: stepName,
          stepConfig: stepConfig,
          stepInputParams: stepConfig.params || {},
          stepOutputParams: executionResponse.result?.autoFixResult?.output,
          autofixText: executionResponse.result?.autofixText,
          error_message: executionResponse.error,
          status: (() => {
            if (executionResponse.status === 'success') return 'success';
            if (executionResponse.status === 'failed') return 'failed';
            if (executionResponse.status === 'pending') return 'pending';
            return executionResponse.status;
          })(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        // Track success/failure result
        try {
          const resultType =
            newStepResult.status === 'success' ? 'success' : 'failure';
          const user = await getUserInfo();

          // Extract PR/Issue details from step output for tracking
          const outputParams = newStepResult.stepOutputParams || {};
          const { prUrl, prNumber, issueUrl, issueNumber, repository } =
            extractGitHubResourceDetails(outputParams);

          analytics.captureEvent(
            'insights:auto_remediation:result',
            'auto_remediation',
            {
              attributes: {
                entity: entityRef || 'unknown',
                checkId: checkId || 'unknown',
                issueId: issueId || 'unknown',
                stepType: isRecommendation ? 'recommendation' : stepType,
                resultType,
                user,
                // Include GitHub resource details for tracking adoption
                ...(prUrl && { pullRequestUrl: prUrl }),
                ...(prNumber && { pullRequestNumber: prNumber }),
                ...(issueUrl && { githubIssueUrl: issueUrl }),
                ...(issueNumber && { githubIssueNumber: issueNumber }),
                ...(repository && { repository }),
              },
            },
          );
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('failed to capture analytic event:', error);
        }
        if (stepName === recommendationStep.handler) {
          setState(prev => ({
            ...prev,
            executing: false,
            recommendationResult: newStepResult,
          }));
        } else if (stepName === autofixStep.handler) {
          setState(prev => ({
            ...prev,
            executing: false,
            autofixResult: newStepResult,
          }));
        }
        // Clear optimistic override and trigger list refetch so badge reflects real state
        setOptimisticStatuses(prev => {
          const next = { ...prev };
          delete next[issueId];
          return next;
        });
        setRemediationVersion(v => v + 1);
      } catch (error) {
        // Clear optimistic override on error
        setOptimisticStatuses(prev => {
          const next = { ...prev };
          delete next[issueId];
          return next;
        });
        setRemediationVersion(v => v + 1);
        setState(prev => ({
          ...prev,
          executing: false,
          error:
            error instanceof Error ? error.message : 'Failed to execute step',
        }));
      }
    },
    [
      issueIds,
      issueId,
      recommendationStep.handler,
      autofixStep.handler,
      yardstickApi,
      entityRef,
      checkId,
      analytics,
      getUserInfo,
      state.recommendationResult,
      state.autofixResult,
      setOptimisticStatuses,
      setRemediationVersion,
    ],
  );

  // Initialize on mount
  useEffect(() => {
    fetchStepResults();
    return undefined;
  }, [fetchStepResults]);

  const recommendationCompleted =
    state.recommendationResult &&
    (state.recommendationResult.status === 'completed' ||
      state.recommendationResult.status === 'success');

  const recommendationFailed =
    state.recommendationResult &&
    state.recommendationResult.status === 'failed';

  const recommendationPending =
    state.recommendationResult &&
    (state.recommendationResult.status === 'pending' ||
      state.recommendationResult.status === 'in_progress');

  const autofixCompleted =
    state.autofixResult &&
    (state.autofixResult.status === 'completed' ||
      state.autofixResult.status === 'success');

  const autofixFailed =
    state.autofixResult && state.autofixResult.status === 'failed';

  const autofixPending =
    state.autofixResult &&
    (state.autofixResult.status === 'pending' ||
      state.autofixResult.status === 'in_progress');

  // Poll for updates when steps are pending
  useEffect(() => {
    if (recommendationPending || autofixPending) {
      const pollInterval = setInterval(() => {
        fetchStepResults();
      }, 30000); // Poll every 30 seconds

      return () => clearInterval(pollInterval);
    }
    return undefined;
  }, [recommendationPending, autofixPending, fetchStepResults]);

  // Render horizontal progress indicator
  const renderProgress = () => {
    const steps: Array<{
      label: string;
      completed: boolean;
      active: boolean;
      failed: boolean;
      pending: boolean;
    }> = [
      {
        label: 'Generate a recommendation',
        completed: !!recommendationCompleted,
        active:
          !recommendationCompleted &&
          !recommendationFailed &&
          !recommendationPending,
        failed: !!recommendationFailed,
        pending: !!recommendationPending,
      },
      {
        label: 'Review recommendation',
        completed: !!autofixCompleted,
        active:
          !!recommendationCompleted &&
          !autofixCompleted &&
          !autofixFailed &&
          !autofixPending,
        failed: !!autofixFailed,
        pending: !!autofixPending,
      },
      {
        label: 'View in GitHub',
        completed: !!autofixCompleted,
        active: !!autofixCompleted,
        failed: false,
        pending: false,
      },
    ];

    return (
      <Box mb={3}>
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          mb={2}
        >
          {steps.map((step, index) => {
            const getBackgroundColor = () => {
              if (step.completed) return '#0066CC';
              if (step.failed) return '#d32f2f';
              if (step.pending) return '#ff9800';
              if (step.active) return '#0066CC';
              return 'transparent';
            };

            const getIcon = () => {
              if (step.completed) return '✓';
              if (step.failed) return '✕';
              if (step.pending) return '⋯';
              return index + 1;
            };

            const getTextColor = () => {
              if (step.completed || step.active)
                return theme.palette.type === 'dark' ? '#FFFFFF' : '#000000';
              if (step.failed) return '#d32f2f';
              if (step.pending) return '#ff9800';
              return theme.palette.text.secondary;
            };

            const getBorderStyle = () => {
              if (step.completed || step.active) return 'none';
              return `2px solid ${
                theme.palette.type === 'dark'
                  ? 'rgba(255, 255, 255, 0.3)'
                  : 'rgba(0, 0, 0, 0.3)'
              }`;
            };

            return (
              <React.Fragment key={index}>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  flex="0 0 auto"
                >
                  <Box
                    width={32}
                    height={32}
                    borderRadius="50%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bgcolor={getBackgroundColor()}
                    border={getBorderStyle()}
                    color="white"
                    fontWeight="bold"
                    fontSize="14px"
                    flexShrink={0}
                  >
                    {getIcon()}
                  </Box>
                  <Typography
                    variant="body2"
                    style={{
                      marginTop: '6px',
                      fontWeight: step.active || step.completed ? 700 : 400,
                      fontFamily:
                        step.active || step.completed
                          ? FontFamily.Bold
                          : FontFamily.Regular,
                      color: getTextColor(),
                      fontSize: '13px',
                      textAlign: 'center',
                      maxWidth: '140px',
                      lineHeight: '18px',
                    }}
                  >
                    {step.label}
                  </Typography>
                </Box>
                {index < steps.length - 1 && (
                  <Box
                    flex={1}
                    height="2px"
                    bgcolor={
                      // eslint-disable-next-line no-nested-ternary
                      step.completed
                        ? '#0066CC'
                        : theme.palette.type === 'dark'
                        ? 'rgba(255, 255, 255, 0.3)'
                        : 'rgba(0, 0, 0, 0.3)'
                    }
                    style={{
                      marginTop: '16px',
                      marginLeft: '8px',
                      marginRight: '8px',
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>
    );
  };

  // Render content based on current phase
  const renderContent = () => {
    // Handle failed recommendation
    if (recommendationFailed) {
      return (
        <Box mb={3}>
          <Alert severity="error" style={{ marginBottom: '16px' }}>
            Failed to generate recommendation
            {state.recommendationResult?.error_message && (
              <>: {state.recommendationResult.error_message}</>
            )}
          </Alert>
          <Typography variant="body2" color="textSecondary">
            Click "Retry" to try generating the recommendation again.
          </Typography>
        </Box>
      );
    }

    // Handle pending recommendation
    if (recommendationPending) {
      return (
        <Box mb={3}>
          <Alert severity="info" style={{ marginBottom: '16px' }}>
            Recommendation generation is{' '}
            {state.recommendationResult?.status === 'pending'
              ? 'queued'
              : 'in progress'}
            ...
          </Alert>
          <Typography variant="body2" color="textSecondary">
            Please wait while the recommendation is being generated. This page
            will automatically update.
          </Typography>
        </Box>
      );
    }

    // PHASE 1: Generate recommendation
    if (!recommendationCompleted) {
      return (
        <Box mb={3}>
          <Typography
            variant="body1"
            style={{
              marginBottom: '16px',
              fontSize: '16px',
              lineHeight: '24px',
              color: theme.palette.text.primary,
            }}
          >
            We can automatically generate a recommended solution for this
            finding.
          </Typography>
          <Typography
            variant="body2"
            style={{
              fontSize: '14px',
              lineHeight: '20px',
              color: theme.palette.text.secondary,
            }}
          >
            If you agree with the generated recommendation, we can create a pull
            request for the updates for you.
          </Typography>
        </Box>
      );
    }

    if (recommendationCompleted && !autofixCompleted) {
      return (
        <Box mb={3}>
          <Typography
            variant="h6"
            style={{
              marginBottom: '8px',
              fontSize: '18px',
              fontWeight: 700,
            }}
          >
            Review Recommendation
          </Typography>
          <Typography
            variant="body2"
            style={{
              marginBottom: '16px',
              fontSize: '14px',
              lineHeight: '20px',
              color: theme.palette.text.secondary,
            }}
          >
            Please review the recommended changes below. If you'd like to use
            this recommendation, click the button to create a GitHub pull
            request.
          </Typography>
          {state.recommendationResult?.autofixText ? (
            <Box
              style={{
                backgroundColor:
                  theme.palette.type === 'dark' ? '#2D2D2D' : '#F5F5F5',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '4px',
                padding: '16px',
              }}
            >
              <ReactMarkdown
                components={{
                  a: linkProps => (
                    <Link
                      href={linkProps.href}
                      component="a"
                      color="primary"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {linkProps.children}
                    </Link>
                  ),
                  code: ({ node, inline, ...codeProps }) => (
                    <code
                      {...codeProps}
                      style={{
                        backgroundColor:
                          theme.palette.type === 'dark' ? '#1E1E1E' : '#E8E8E8',
                        color: theme.palette.text.primary,
                        padding: inline ? '2px 6px' : '0',
                        borderRadius: '3px',
                        fontSize: '13px',
                        fontFamily: 'monospace',
                        wordBreak: 'break-all',
                        whiteSpace: 'pre-wrap',
                      }}
                    />
                  ),
                  pre: ({ node, ...preProps }) => (
                    <pre
                      {...preProps}
                      style={{
                        backgroundColor:
                          theme.palette.type === 'dark' ? '#1E1E1E' : '#2D2D2D',
                        color:
                          theme.palette.type === 'dark' ? '#D4D4D4' : '#F8F8F8',
                        padding: '12px',
                        borderRadius: '4px',
                        overflowX: 'auto',
                        fontSize: '13px',
                        fontFamily: 'monospace',
                        margin: '8px 0',
                        maxWidth: '100%',
                      }}
                    />
                  ),
                }}
              >
                {state.recommendationResult.autofixText}
              </ReactMarkdown>
            </Box>
          ) : (
            <Typography
              variant="body2"
              style={{ color: theme.palette.text.secondary, fontSize: '14px' }}
            >
              No recommendation available.
            </Typography>
          )}
        </Box>
      );
    }

    // Handle failed autofix
    if (autofixFailed) {
      return (
        <Box mb={3}>
          <Alert severity="error" style={{ marginBottom: '16px' }}>
            Failed to apply auto remediation
            {state.autofixResult?.error_message && (
              <>: {state.autofixResult.error_message}</>
            )}
          </Alert>
          <Typography variant="body2" color="textSecondary">
            The auto remediation could not be applied. Please try again or
            contact support.
          </Typography>
        </Box>
      );
    }

    // Handle pending autofix
    if (autofixPending) {
      return (
        <Box mb={3}>
          <Alert severity="info" style={{ marginBottom: '16px' }}>
            Auto remediation is{' '}
            {state.autofixResult?.status === 'pending'
              ? 'queued'
              : 'in progress'}
            ...
          </Alert>
          <Typography variant="body2" color="textSecondary">
            Please wait while the auto remediation is being applied. This page
            will automatically update.
          </Typography>
        </Box>
      );
    }

    if (autofixCompleted && state.autofixResult) {
      return (
        <Box mb={3}>
          <Typography
            variant="h6"
            style={{
              marginBottom: '16px',
              fontSize: '18px',
              fontWeight: 700,
            }}
          >
            A pull request with the recommended updates has been opened.
          </Typography>
          {state.autofixResult.autofixText ? (
            <Box
              style={{
                backgroundColor:
                  theme.palette.type === 'dark' ? '#2D2D2D' : '#F5F5F5',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '4px',
                padding: '16px',
              }}
            >
              <ReactMarkdown
                components={{
                  a: linkProps => (
                    <Link
                      href={linkProps.href}
                      component="a"
                      color="primary"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {linkProps.children}
                    </Link>
                  ),
                  code: ({ node, inline, ...codeProps }) => (
                    <code
                      {...codeProps}
                      style={{
                        backgroundColor:
                          theme.palette.type === 'dark' ? '#1E1E1E' : '#E8E8E8',
                        color: theme.palette.text.primary,
                        padding: inline ? '2px 6px' : '0',
                        borderRadius: '3px',
                        fontSize: '13px',
                        fontFamily: 'monospace',
                        wordBreak: 'break-all',
                        whiteSpace: 'pre-wrap',
                      }}
                    />
                  ),
                  pre: ({ node, ...preProps }) => (
                    <pre
                      {...preProps}
                      style={{
                        backgroundColor:
                          theme.palette.type === 'dark' ? '#1E1E1E' : '#2D2D2D',
                        color:
                          theme.palette.type === 'dark' ? '#D4D4D4' : '#F8F8F8',
                        padding: '12px',
                        borderRadius: '4px',
                        overflowX: 'auto',
                        fontSize: '13px',
                        fontFamily: 'monospace',
                        margin: '8px 0',
                        maxWidth: '100%',
                      }}
                    />
                  ),
                }}
              >
                {state.autofixResult.autofixText}
              </ReactMarkdown>
            </Box>
          ) : (
            <Typography
              variant="body2"
              style={{ color: theme.palette.text.secondary, fontSize: '14px' }}
            >
              No auto remediation result available.
            </Typography>
          )}
        </Box>
      );
    }

    return null;
  };

  // Render action buttons
  const renderButtons = () => {
    const allCompleted = autofixCompleted;

    if (allCompleted) {
      const pullRequestUrl =
        state.autofixResult?.stepOutputParams?.pullRequestUrl;

      return (
        <Box mt={2}>
          <Box display="flex" flexWrap="wrap" style={{ gap: '8px' }}>
            {pullRequestUrl && (
              <ActionButton
                onClick={() => window.open(pullRequestUrl, '_blank')}
              >
                View pull request in GitHub
              </ActionButton>
            )}
            {onClose && (
              <ActionButton onClick={onClose} variant="outline">
                Close
              </ActionButton>
            )}
          </Box>
        </Box>
      );
    }

    const getButtonText = () => {
      if (recommendationFailed) {
        return 'Retry';
      }
      if (!recommendationCompleted) {
        return 'Generate';
      }
      if (autofixFailed) {
        return 'Retry';
      }
      if (autofixStep.handler.includes('github-issue')) {
        return 'Create an issue';
      }
      if (autofixStep.handler.includes('pull-request')) {
        return 'Open a pull request';
      }
      return 'Apply AutoFix';
    };

    const handleClick = () => {
      if (!recommendationCompleted) {
        executeStep(recommendationStep.handler, recommendationStep);
      } else {
        executeStep(autofixStep.handler, autofixStep);
      }
    };

    return (
      <Box mt={2} display="flex" flexWrap="wrap" style={{ gap: '8px' }}>
        <ActionButton
          onClick={handleClick}
          disabled={
            state.executing || !!recommendationPending || !!autofixPending
          }
        >
          {state.executing ? 'Executing...' : getButtonText()}
        </ActionButton>
        {onClose && (
          <ActionButton onClick={onClose} variant="outline">
            Cancel
          </ActionButton>
        )}
      </Box>
    );
  };

  return (
    <Box style={{ padding: '16px 0' }}>
      {state.loading && (
        <Box mb={2}>
          <LoadingState text="Loading auto remediation status..." />
        </Box>
      )}

      {!state.loading && state.fetchError && (
        <Box mb={2}>
          <Alert severity="error">{state.fetchError}</Alert>
          {onClose && (
            <Box mt={2}>
              <ActionButton onClick={onClose} variant="outline">
                Close
              </ActionButton>
            </Box>
          )}
        </Box>
      )}

      {!state.loading && !state.fetchError && (
        <>
          {state.executing && (
            <Box mb={2}>
              <LoadingState text="Executing auto remediation step..." />
            </Box>
          )}

          {renderProgress()}
          {renderContent()}
          {renderButtons()}
        </>
      )}
    </Box>
  );
};
